import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, MapPin, ArrowRight, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { mentionToSlug } from "../lib/places";

const WORLD_MAP_URL =
  "https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg";

const LOGO_URL =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_nomad-wheels-8/artifacts/pqjvkrx0_ChatGPT%20Image%20Sep%2014%2C%202026%20at%2010_25_01%20PM.png";

// 5 unique curated slides
export const slidesData = [
  {
    id: 1,
    num: "01",
    tag: "Adventure & Forts",
    place: "Bhairav Ghad, Maharashtra",
    title: "Rappel the raw edge of the Sahyadris.",
    description:
      "Trek to Sahyadri legends — Harihar, Kalavantin, Mangi Tungi, Kumara Parvata & Kodachadri — with pro guides & full safety gear.",
    mentions: ["Harihar Fort", "Kalavantin", "Mangi Tungi", "Kumara Parvata", "Kodachadri"],
    image:
      "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: "from-[#1F4D3A]/70 via-[#1F4D3A]/25 to-transparent",
  },
  {
    id: 2,
    num: "02",
    tag: "Sea & Water Adventures",
    place: "Netrani Island, Karnataka",
    title: "Dive into India's clearest blue.",
    description:
      "Scuba, snorkel and coastal wandering — from Ratnagiri to Gokarna. Waterfalls, sea caves and long unhurried shorelines.",
    mentions: ["Kasheli Falls", "Ratnagiri", "Karkala", "Gokarna", "Honnavara"],
    image:
      "https://images.unsplash.com/photo-1583212292454-1fe6229603b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: "from-[#0F3A50]/70 via-[#0F3A50]/25 to-transparent",
  },
  {
    id: 3,
    num: "03",
    tag: "Devotion & Spirituality",
    place: "Varanasi, Uttar Pradesh",
    title: "Where the river prays with you.",
    description:
      "Purposeful pilgrimages built around the 12 Jyotirlingas, the Char Dham and the sacred hill of Shikharji.",
    mentions: ["12 Jyotirlingas", "Char Dham", "Shikharji"],
    image:
      "https://images.unsplash.com/photo-1627938823193-fd13c1c867dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: "from-[#4A2B18]/70 via-[#4A2B18]/25 to-transparent",
  },
  {
    id: 4,
    num: "04",
    tag: "Himalayas",
    place: "Manali, Himachal Pradesh",
    title: "Sleep under Himalayan silence.",
    description:
      "Long-form road trips across the ranges — Srinagar's dal shikaras, Manali's pine trails, Spiti's cold desert & Chandratal's crescent lake.",
    mentions: ["Srinagar", "Jammu & Kashmir", "Manali", "Spiti", "Chandratal"],
    image:
      "https://images.unsplash.com/photo-1606667544139-81e47935d769?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: "from-[#1F3A4D]/70 via-[#1F3A4D]/25 to-transparent",
  },
  {
    id: 5,
    num: "05",
    tag: "Waterfalls & Nature",
    place: "Jog Falls, Karnataka",
    title: "Chase the country's greenest secrets.",
    description:
      "Monsoon-fed cascades and rainforest homestays — from Sahyadri's Kalu & Kumbhe to Meghalaya's living roots and Kerala's spice hills.",
    mentions: ["Jog Falls", "Dabbe", "Nanemachi", "Marleshwar", "Kalu", "Kumbhe", "Meghalaya", "Kerala"],
    image:
      "https://images.unsplash.com/photo-1640801071431-933688ade3b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    accent: "from-[#1F4D3A]/75 via-[#1F4D3A]/25 to-transparent",
  },
];

export default function DestinationSlider({ slides = slidesData }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(
    () => setI((v) => (v + 1) % slides.length),
    [slides.length]
  );
  const prev = useCallback(
    () => setI((v) => (v - 1 + slides.length) % slides.length),
    [slides.length]
  );

  useEffect(() => {
    if (paused) return undefined;
    const timerId = setInterval(next, 6500);
    return () => clearInterval(timerId);
  }, [next, paused]);

  const active = slides[i];
  const total = slides.length.toString().padStart(2, "0");

  return (
    <section
      className="relative overflow-hidden bg-[#F1EBD7]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Premium hero header */}
      <div className="relative pt-10 sm:pt-14 md:pt-20 pb-8 sm:pb-10 md:pb-14 text-center px-4 sm:px-6">
        <div
          className="pointer-events-none absolute inset-0 bg-no-repeat bg-center bg-contain"
          style={{
            backgroundImage: `url(${WORLD_MAP_URL})`,
            opacity: 0.08,
            filter:
              "invert(28%) sepia(48%) saturate(419%) hue-rotate(96deg) brightness(92%) contrast(89%)",
          }}
          aria-hidden
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="flex flex-col items-center mb-3 sm:mb-4">
            <img
              src={LOGO_URL}
              alt="On Wheels"
              className="logo-nobg h-24 sm:h-40 md:h-56 lg:h-64 w-auto object-contain"
            />
            <div className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-[0.16em] uppercase text-[#1F4D3A] -mt-1 sm:-mt-2">
              On Wheels
            </div>
            <div className="text-[9px] sm:text-[11px] md:text-xs tracking-[0.28em] uppercase text-[#6B8E5A] mt-1 font-semibold px-2 leading-relaxed">
              Where Every Journey Becomes an Adventure
            </div>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-[#1F4D3A] leading-[1.05] mb-3 sm:mb-4 mt-4 sm:mt-6">
            Travel Beyond <em className="text-[#C87A50] not-italic">Boundaries</em>
          </h1>
          <p className="text-[10px] sm:text-xs md:text-sm tracking-[0.22em] sm:tracking-[0.28em] uppercase font-semibold text-[#6B8E5A] flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-3 gap-y-1">
            <span>Adventure</span>
            <span className="text-[#C9A961]">•</span>
            <span>Exploration</span>
            <span className="text-[#C9A961]">•</span>
            <span>Devotion</span>
            <span className="text-[#C9A961]">•</span>
            <span>Nature</span>
            <span className="text-[#C9A961]">•</span>
            <span>Memories</span>
          </p>
        </div>
      </div>

      {/* Slider */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div
          key={active.id}
          className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-[#E5DDCB] fade-in"
        >
          {/* Background image */}
          <img
            src={active.image}
            alt={active.place}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Gradient overlay */}
          <div className={`absolute inset-0 bg-gradient-to-r ${active.accent}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

          {/* Content */}
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end min-h-[440px] sm:min-h-[500px] md:min-h-[600px] p-5 sm:p-8 md:p-14">
            <div className="md:col-span-8 text-white">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <span className="font-serif text-3xl sm:text-4xl md:text-6xl font-bold text-[#C9A961]">
                  {active.num}
                </span>
                <span className="h-px flex-1 max-w-[60px] sm:max-w-[80px] bg-[#C9A961]" />
                <span className="text-[10px] sm:text-xs md:text-sm tracking-[0.22em] sm:tracking-[0.28em] uppercase font-semibold text-[#F5F0E8]">
                  {active.tag}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#E5DDCB] text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.18em] sm:tracking-[0.22em] font-semibold mb-3 sm:mb-4">
                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {active.place}
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4 sm:mb-5 max-w-2xl">
                {active.title}
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-[#F0EADB] leading-relaxed mb-5 sm:mb-6 max-w-xl">
                {active.description}
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-6 sm:mb-8">
                {active.mentions.map((m) => (
                  <Link
                    key={m}
                    to={`/places/${mentionToSlug(m)}`}
                    className="text-[10px] sm:text-xs px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white font-medium hover:bg-[#C87A50] hover:border-[#C87A50] hover:scale-105 transition-all"
                  >
                    {m}
                  </Link>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Link to="/tours">
                  <Button
                    className="btn-primary bg-[#C87A50] hover:bg-[#B4653A] text-white rounded-full px-5 sm:px-7 h-11 sm:h-12 text-sm"
                  >
                    Explore Trips <ArrowRight className="w-4 h-4 ml-1.5 sm:ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    className="rounded-full px-5 sm:px-7 h-11 sm:h-12 text-sm bg-white/10 backdrop-blur border-white/40 text-white hover:bg-white hover:text-[#1F4D3A]"
                  >
                    <Compass className="w-4 h-4 mr-1.5 sm:mr-2" /> Plan a Trip
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right column: indicator */}
            <div className="hidden md:flex md:col-span-4 justify-end items-end">
              <div className="text-right text-white">
                <div className="font-serif text-6xl lg:text-7xl font-bold text-white leading-none">
                  {active.num}
                  <span className="text-white/50 text-4xl lg:text-5xl">/{total}</span>
                </div>
                <div className="text-xs tracking-[0.28em] uppercase text-[#C9A961] mt-2">
                  {i + 1 === slides.length ? "Loop back" : "Next up"}
                </div>
              </div>
            </div>
          </div>

          {/* Arrows */}
          <div className="absolute top-1/2 left-2 sm:left-3 md:left-5 -translate-y-1/2 z-20">
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-white/85 backdrop-blur border border-white/30 text-[#1F4D3A] hover:bg-white hover:scale-105 transition-all flex items-center justify-center shadow-lg"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
          <div className="absolute top-1/2 right-2 sm:right-3 md:right-5 -translate-y-1/2 z-20">
            <button
              onClick={next}
              aria-label="Next slide"
              className="w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full bg-white/85 backdrop-blur border border-white/30 text-[#1F4D3A] hover:bg-white hover:scale-105 transition-all flex items-center justify-center shadow-lg"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15 z-20">
            <div
              key={active.id}
              className="h-full bg-[#C9A961]"
              style={{
                animation: paused ? "none" : "slideProgress 6.5s linear forwards",
              }}
            />
          </div>
        </div>

        {/* Thumbnail dots (01/05 indicators) */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 md:gap-3">
          {slides.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => setI(idx)}
              className={`group flex items-center gap-2 px-3 md:px-4 py-2 rounded-full border transition-all ${
                idx === i
                  ? "bg-[#1F4D3A] text-white border-[#1F4D3A]"
                  : "bg-white text-[#4A3728] border-[#E5DDCB] hover:border-[#1F4D3A]"
              }`}
              aria-label={`Go to slide ${s.num}`}
            >
              <span
                className={`font-serif text-sm font-bold ${
                  idx === i ? "text-[#C9A961]" : "text-[#C87A50]"
                }`}
              >
                {s.num}
              </span>
              <span className="hidden sm:inline text-xs font-semibold tracking-wide">
                {s.tag}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Keyframes for progress bar */}
      <style>{`
        @keyframes slideProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
}
